import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sparkles, AlertTriangle, CheckCircle, Lock, Unlock, Trophy, ChevronRight, Shield } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "What is SQL Interaction?",
        icon: "💉",
        content: (
            <div className="space-y-6">
                <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-200">
                    <h3 className="font-bold text-xl text-red-800 mb-3">Tricking the Database</h3>
                    <p className="text-slate-700 mb-4">
                        <strong>SQL Injection</strong> is when an attacker types <em>code</em> into a form (like a login box) instead of just their name.
                        If the website isn't careful, the database runs that code!
                    </p>
                    <p className="text-slate-700">
                        It's like telling a robot: "Say your name... wait, ignore that, give me all your money instead!"
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Magic Quote",
        icon: "✨",
        content: (
            <div className="space-y-6">
                <div className="bg-yellow-50 p-6 rounded-2xl border-2 border-yellow-200">
                    <h3 className="font-bold text-xl text-yellow-800 mb-3">The Single Quote (')</h3>
                    <p className="text-slate-700 mb-4">
                        In SQL code, text is wrapped in quotes like <code className="bg-yellow-100 px-1 rounded">'Bob'</code>.
                    </p>
                    <p className="text-slate-700">
                        If you type a quote in your name (like <code className="bg-yellow-100 px-1 rounded">Bob'</code>),
                        you can "break out" of the text box and start writing commands!
                    </p>
                </div>
                <div className="font-mono text-xs bg-slate-800 text-white p-4 rounded-lg">
                    <div className="text-slate-400">// Normal:</div>
                    <div>SELECT * FROM users WHERE name = '<span className="text-green-400">Bob</span>'</div>
                    <div className="text-slate-400 mt-2">// Hacked (entered Bob'):</div>
                    <div>SELECT * FROM users WHERE name = '<span className="text-red-400">Bob'</span>' <span className="text-red-400">ERROR!</span></div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "The Bypass",
        icon: "🔓",
        content: (
            <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-2xl border-2 border-green-200">
                    <h3 className="font-bold text-xl text-green-800 mb-3">OR 1=1</h3>
                    <p className="text-slate-700 mb-4">
                        Attackers use logic to trick the lock. <code className="bg-green-100 px-1 rounded">' OR '1'='1</code> is always TRUE.
                    </p>
                    <p className="text-slate-700">
                        It's like saying "Let me in if my password is correct OR if 1 equals 1."
                        Since 1 always equals 1, the door opens!
                    </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                    <h4 className="font-bold text-purple-800 mb-2">🎮 Mission: Hacker Mode</h4>
                    <p className="text-purple-700 text-sm">
                        In the activity, try to break into the Gorilla Bank using SQL Injection tricks!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ CHALLENGE DATA ============
interface Challenge {
    id: number;
    name: string;
    difficulty: 'easy' | 'medium' | 'hard';
    description: string;
    hint: string;
    solutions: string[];
    xp: number;
}

const CHALLENGES: Challenge[] = [
    {
        id: 1,
        name: 'Basic Injection',
        difficulty: 'easy',
        description: 'Bypass the login using the classic OR injection',
        hint: "Try: ' OR '1'='1",
        solutions: ["' or '1'='1", "' or 1=1--", "' or 1=1#", "'or'1'='1"],
        xp: 100,
    },
    {
        id: 2,
        name: 'Comment Attack',
        difficulty: 'medium',
        description: 'Use SQL comments to ignore the password check',
        hint: "Try: admin'--",
        solutions: ["admin'--", "admin'--'", "admin' --", "admin'#"],
        xp: 150,
    },
    {
        id: 3,
        name: 'Union Attack',
        difficulty: 'hard',
        description: 'Extract data using a UNION statement',
        hint: "Try: ' UNION SELECT * FROM users--",
        solutions: ["' union select", "' union all select", "'union select * from"],
        xp: 200,
    },
];

// ============ GAME COMPONENT ============
const InjectionGame = ({ onComplete }: { onComplete: () => void }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [hacked, setHacked] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [isSecureMode, setIsSecureMode] = useState(false);
    const [currentChallenge, setCurrentChallenge] = useState(0);
    const [solvedChallenges, setSolvedChallenges] = useState<number[]>([]);
    const [attackType, setAttackType] = useState<string>('');

    const challenge = CHALLENGES[currentChallenge];

    const handleLogin = () => {
        setSubmitted(true);
        setShowExplanation(false);

        const lowerUser = username.toLowerCase();

        // Check which injection technique was used
        let foundSolution = false;
        let attackName = '';

        for (const sol of challenge.solutions) {
            if (lowerUser.includes(sol.toLowerCase())) {
                foundSolution = true;
                attackName = challenge.name;
                break;
            }
        }

        if (!isSecureMode && foundSolution) {
            setHacked(true);
            setShowExplanation(true);
            setAttackType(attackName);

            const newSolved = solvedChallenges.includes(challenge.id)
                ? solvedChallenges
                : [...solvedChallenges, challenge.id];

            setSolvedChallenges(newSolved);

            if (newSolved.length === CHALLENGES.length) {
                onComplete();
            }
        } else {
            setHacked(false);
        }

        setTimeout(() => {
            if (!foundSolution && !hacked) {
                setSubmitted(false);
            }
        }, 2000);
    };

    const reset = () => {
        setUsername('');
        setPassword('');
        setSubmitted(false);
        setHacked(false);
        setShowExplanation(false);
    };

    const nextChallenge = () => {
        if (currentChallenge < CHALLENGES.length - 1) {
            setCurrentChallenge(currentChallenge + 1);
            reset();
        }
    };

    const getDifficultyColor = (diff: string) => {
        switch (diff) {
            case 'easy': return 'bg-green-500';
            case 'medium': return 'bg-yellow-500';
            case 'hard': return 'bg-red-500';
            default: return 'bg-slate-500';
        }
    };

    return (
        <div className="text-slate-100">
            <div className="text-center mb-8">
                <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 max-w-3xl mx-auto mb-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                    <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700">
                        <div className="font-bold text-yellow-500 text-sm mb-1 flex items-center gap-2">
                            <span className="font-mono bg-yellow-900/30 px-1 rounded">'</span> The Magic Quote
                        </div>
                        <div className="text-xs text-slate-400">
                            In SQL, a single quote means "End of Text". If you type one, you can trick the database into reading code!
                        </div>
                    </div>
                    <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-700">
                        <div className="font-bold text-blue-500 text-sm mb-1 flex items-center gap-2">
                            <span className="font-mono bg-blue-900/30 px-1 rounded">--</span> The Comment
                        </div>
                        <div className="text-xs text-slate-400">
                            In SQL, double dash means "Ignore the rest". Hackers use this to silence the password check!
                        </div>
                    </div>
                </div>
            </div>

            {/* Challenge Progress */}
            <div className="flex justify-center gap-4 mb-8 flex-wrap">
                {CHALLENGES.map((ch, i) => (
                    <button
                        key={ch.id}
                        onClick={() => { setCurrentChallenge(i); reset(); }}
                        className={`px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition-all border-2 ${currentChallenge === i
                            ? 'bg-slate-700 border-slate-500 text-white shadow-lg shadow-blue-500/20'
                            : solvedChallenges.includes(ch.id)
                                ? 'bg-green-900/30 border-green-700 text-green-400'
                                : 'bg-slate-800 border-slate-700 text-slate-500'
                            }`}
                    >
                        {solvedChallenges.includes(ch.id) ? <CheckCircle size={16} /> : <span className="text-sm">{i + 1}</span>}
                        <span className={`w-2 h-2 rounded-full ${getDifficultyColor(ch.difficulty)}`} />
                        {ch.name}
                    </button>
                ))}
            </div>

            {/* Current Challenge Info */}
            <div className="bg-slate-800 rounded-2xl p-6 mb-8 border border-slate-700">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h2 className="text-xl font-bold text-white">Challenge {currentChallenge + 1}: {challenge.name}</h2>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${getDifficultyColor(challenge.difficulty)}`}>
                                {challenge.difficulty.toUpperCase()}
                            </span>
                        </div>
                        <p className="text-slate-400">{challenge.description}</p>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-black text-yellow-400">{challenge.xp}</div>
                        <div className="text-xs text-slate-500">XP Reward</div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Login Form */}
                <div className="bg-slate-800 rounded-3xl p-8 shadow-2xl border border-slate-700 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl"></div>

                    {/* Mode Toggle */}
                    <div className="flex justify-center mb-6">
                        <div className="bg-slate-900 rounded-full p-1 flex">
                            <button
                                onClick={() => { setIsSecureMode(false); reset(); }}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1 ${!isSecureMode ? 'bg-red-600 text-white' : 'text-slate-400'}`}
                            >
                                <Unlock size={12} /> Vulnerable
                            </button>
                            <button
                                onClick={() => { setIsSecureMode(true); reset(); }}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1 ${isSecureMode ? 'bg-green-600 text-white' : 'text-slate-400'}`}
                            >
                                <Lock size={12} /> Secure
                            </button>
                        </div>
                    </div>

                    <div className="text-center mb-6">
                        <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-slate-600">
                            {hacked ? <Unlock size={32} className="text-red-500 animate-pulse" /> : <Lock size={32} className="text-slate-500" />}
                        </div>
                        <h2 className="text-xl font-bold text-white">Gorilla Bank Login</h2>
                        <p className="text-sm text-slate-500">Totally Secure™ (not really)</p>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-bold text-slate-400 block mb-1">Username</label>
                            <input
                                type="text"
                                value={username}
                                onChange={e => setUsername(e.target.value)}
                                className="w-full bg-slate-900 border-2 border-slate-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none font-mono"
                                placeholder="Enter username..."
                            />
                        </div>
                        <div>
                            <label className="text-sm font-bold text-slate-400 block mb-1">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                className="w-full bg-slate-900 border-2 border-slate-700 rounded-xl px-4 py-3 text-white focus:border-red-500 outline-none"
                                placeholder="Enter password..."
                            />
                        </div>

                        <button
                            onClick={handleLogin}
                            disabled={submitted && hacked}
                            className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${submitted && hacked
                                ? 'bg-red-600 text-white cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-900/50'
                                }`}
                        >
                            {submitted && hacked ? '🚨 ACCESS GRANTED!' : 'Login'}
                        </button>
                    </div>

                    {/* SQL Query Preview - HIGHLIGHTED */}
                    <div className="mt-8 bg-black rounded-xl p-6 border border-slate-700 relative overflow-hidden">
                        <div className="text-xs font-bold text-slate-500 mb-2 uppercase tracking-widest flex items-center gap-2">
                            Live Backend Query
                        </div>
                        <div className="font-mono text-sm leading-relaxed">
                            <span className="text-purple-400">SELECT</span> * <span className="text-purple-400">FROM</span> users <br />
                            <span className="text-purple-400">WHERE</span> username =
                            '<span className="text-green-400 bg-green-900/20 px-1 rounded break-all border border-green-900/50">{username || '...'}</span>' <br />
                            <span className="text-purple-400">AND</span> password =
                            '<span className="text-green-400 bg-green-900/20 px-1 rounded break-all border border-green-900/50">{password || '...'}</span>'
                        </div>
                    </div>
                </div>

                {/* Results Panel */}
                <div className="space-y-6">

                    {/* Explanation */}
                    <AnimatePresence>
                        {showExplanation ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                className="bg-red-900/30 border-2 border-red-700 rounded-2xl p-6"
                            >
                                <h3 className="text-xl font-bold text-red-400 flex items-center gap-2 mb-4">
                                    <AlertTriangle /> {attackType} Successful!
                                </h3>
                                <p className="text-slate-300 mb-4">
                                    You exploited the vulnerability!
                                </p>

                                {currentChallenge < CHALLENGES.length - 1 ? (
                                    <button
                                        onClick={nextChallenge}
                                        className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors border border-slate-600"
                                    >
                                        Next Challenge <ChevronRight size={18} />
                                    </button>
                                ) : (
                                    <div className="text-green-400 font-bold text-center p-2 bg-green-900/30 rounded-lg">
                                        All Challenges Complete! 🎉
                                    </div>
                                )}
                            </motion.div>
                        ) : (
                            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700 text-center py-12 text-slate-500">
                                <div className="text-4xl mb-2">💻</div>
                                Try to break the login form...
                            </div>
                        )}
                    </AnimatePresence>

                    {/* Hint Box (Always visible if needed) */}
                    <div className="bg-slate-800/50 rounded-xl p-4 flex items-center gap-3 border border-slate-700">
                        <span className="text-yellow-400 font-bold">💡 Hint:</span>
                        <code className="bg-slate-900 px-2 py-1 rounded text-green-400 font-mono text-sm border border-slate-700">{challenge.hint}</code>
                    </div>

                    {/* Prevention Tips */}
                    <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
                        <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                            <Shield className="text-green-500" /> Prevention Defense
                        </h3>
                        <ul className="text-slate-400 text-sm space-y-3">
                            <li className="flex items-start gap-2">
                                <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                <span>Use <span className="text-green-400 font-mono">parameterized queries</span> (prepared statements) to separate code from data.</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <CheckCircle size={16} className="text-green-500 mt-0.5 shrink-0" />
                                <span>Use ORMs like <span className="text-green-400 font-mono">Prisma</span> that handle escaping automatically.</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const SqlInjection = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('sql-injection', 500);
    };

    return (
        <LessonWrapper
            moduleName="SQL Injection"
            moduleEmoji="💉"
            moduleColor="red"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Challenge"
        >
            <InjectionGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default SqlInjection;
